// The model selects a feature; this registry owns actual platform destinations.
export const NAVIGATION_MESSAGE = 'gutdb_platform_navigation'
export const NAVIGATION_ACK = 'gutdb_platform_navigation_ack'
export const QUERY_KEY = 'zeusQuery'
export const QUERY_FIELDS = ['organism', 'disease', 'tissue', 'dataType']
export const FIELD_LABELS = { keyword: '关键词', organism: '物种', disease: '疾病', tissue: '组织', dataType: '技术' }
const features = ['dataset.search', 'analysis.standard', 'analysis.personalized']
// 与 xBIOS platform-navigation/catalog.ts 的 platformWorkflows 对应。
const BUILTIN_WORKFLOW_PATHS = { 'tcm-microbiota': '/inspiration/tcm-microbiota/create' }
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const text = (value, max) => typeof value === 'string' && value.length <= max
const only = (value, keys) => Object.keys(value).every(key => keys.includes(key))
const invalid = () => { throw new Error('卡片内容无效，请让 AI 重新生成入口。') }

export function validateQuery(value) {
  if (!object(value) || !only(value, ['keyword', ...QUERY_FIELDS])) invalid()
  const query = {}
  if (value.keyword !== undefined) {
    if (!text(value.keyword, 300)) invalid()
    query.keyword = value.keyword.trim()
  }
  for (const field of QUERY_FIELDS) {
    if (value[field] === undefined) continue
    if (!Array.isArray(value[field]) || value[field].length > 10 || value[field].some(item => !text(item, 120) || !item.trim())) invalid()
    // Values come from the live catalog. Some legacy keys contain trailing spaces;
    // changing them here would turn an already matched option into a different value.
    query[field] = [...new Set(value[field])]
  }
  return query
}

function validateUnapplied(value = []) {
  if (!Array.isArray(value) || value.length > 40 || value.some(item => !object(item) || !only(item, ['field', 'value']) || !QUERY_FIELDS.includes(item.field) || !text(item.value, 120))) invalid()
  return value.map(item => ({ field: item.field, value: item.value }))
}

export function validateNavigationCard(value) {
  if (!object(value) || !only(value, ['version', 'feature', 'title', 'description', 'template', 'query', 'unapplied']) ||
      value.version !== 1 || !features.includes(value.feature) || !text(value.title, 120) || !text(value.description, 500)) invalid()
  if (value.feature !== 'dataset.search' && (value.query !== undefined || value.unapplied !== undefined)) invalid()
  if (value.feature !== 'analysis.standard' && value.template !== undefined) invalid()
  const result = { version: 1, feature: value.feature, title: value.title, description: value.description }
  if (value.feature === 'dataset.search') {
    result.query = validateQuery(value.query || {})
    result.unapplied = validateUnapplied(value.unapplied)
  }
  if (value.template !== undefined) {
    const template = value.template
    if (!object(template) || !only(template, ['slug', 'packageVersion']) || typeof template.slug !== 'string' ||
        !/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,119}$/.test(template.slug)) invalid()
    if (template.packageVersion !== undefined && (typeof template.packageVersion !== 'string' || !/^[a-zA-Z0-9][a-zA-Z0-9._+-]{0,79}$/.test(template.packageVersion))) invalid()
    result.template = { ...template }
  }
  return result
}

export async function navigationTarget(raw, listTemplates) {
  const card = validateNavigationCard(raw)
  if (card.feature === 'dataset.search') {
    const encoded = JSON.stringify({ query: card.query, unapplied: card.unapplied })
    if (encoded.length > 7000) invalid()
    return { path: '/dataset', query: { tab: 'query', [QUERY_KEY]: encoded } }
  }
  if (card.feature === 'analysis.personalized') return { path: '/ai-chat' }
  if (!card.template) return { path: '/capabilities' }
  // 页面自带的流程（能力空间 tcmWorkflows）不在模板接口里，也没有版本。
  const builtin = BUILTIN_WORKFLOW_PATHS[card.template.slug]
  if (builtin) {
    if (card.template.packageVersion) invalid()
    return { path: builtin }
  }
  // Revalidate persisted cards at click time, so removed workflows/versions cannot be opened.
  const response = await listTemplates()
  if (response?.data?.code !== 200 || !Array.isArray(response.data.data)) throw new Error('暂时无法确认流程，请稍后重试。')
  const template = response.data.data.find(item => item.slug === card.template.slug)
  if (!template) throw new Error('该流程已不可用，请让 AI 重新推荐。')
  const version = card.template.packageVersion
  if (version && !template.packageVersions?.includes(version)) throw new Error('该流程版本已不可用，请让 AI 重新推荐。')
  return { path: `/inspiration/templates/${encodeURIComponent(template.slug)}/create`, query: version ? { packageVersion: version } : {} }
}

export function parseNavigationQuery(value) {
  if (value === undefined) return null
  if (!text(value, 7000)) invalid()
  let raw
  try { raw = JSON.parse(value) } catch { invalid() }
  if (!object(raw) || !only(raw, ['query', 'unapplied'])) invalid()
  return { query: validateQuery(raw.query), unapplied: validateUnapplied(raw.unapplied) }
}

export function validateQueryOptions(input, options) {
  if (!object(options) || QUERY_FIELDS.some(field => !object(options[field]))) throw new Error('暂时无法核验筛选条件，请重试。')
  const query = input.query.keyword ? { keyword: input.query.keyword } : {}
  const unapplied = [...input.unapplied]
  for (const field of QUERY_FIELDS) {
    query[field] = []
    for (const value of input.query[field] || []) {
      if (Object.prototype.hasOwnProperty.call(options[field], value)) query[field].push(value)
      else unapplied.push({ field, value })
    }
  }
  return { query, unapplied }
}

export function createNavigationHandler({ source, origin, listTemplates, navigate, openProjects }) {
  let busy = false
  return async event => {
    if (event.data?.type !== NAVIGATION_MESSAGE || event.origin !== origin || event.source !== source()) return false
    const { requestId, card } = event.data
    if (typeof requestId !== 'string' || !/^[a-zA-Z0-9-]{1,80}$/.test(requestId)) return true
    const reply = payload => event.source.postMessage({ type: NAVIGATION_ACK, requestId, ...payload }, origin)
    if (busy) { reply({ ok: false, message: '正在打开另一个入口，请稍后重试。' }); return true }
    busy = true
    try {
      const target = await navigationTarget(card, listTemplates)
      const failure = await navigate(target)
      // vue-router returns a navigation failure for cancelled/aborted transitions.
      // Duplicated navigation (16) is already at the intended destination.
      if (failure && failure.type !== 16) throw new Error('页面尚未切换，请先完成当前页面的操作后重试。')
      if (card.feature === 'analysis.personalized') openProjects()
      reply({ ok: true })
    } catch (error) {
      const known = error instanceof Error && /^(卡片|该流程|暂时|页面)/.test(error.message)
      reply({ ok: false, message: known ? error.message : '无法打开入口，请检查登录状态或稍后重试。' })
    } finally { busy = false }
    return true
  }
}
